import { Container, Graphics, Sprite } from 'pixi.js';
import type { PublicPlayerAppearance } from '@push.io/shared';
import { warnCosmeticFallback } from '../fallback-warning.js';
import { AssetManager } from '../AssetManager.js';

type AccessoryLayout = {
  scale: number;
  offsetX?: number;
  offsetY: number;
};

const ACCESSORY_LAYOUTS: Record<string, AccessoryLayout> = {
  cosm_acc_round_glasses: { scale: 1.18, offsetY: -0.12 },
  cosm_acc_sunglasses: { scale: 1.18, offsetY: -0.1 },
  cosm_acc_headband: { scale: 1.18, offsetY: -0.1 },
  cosm_acc_eyepatch: { scale: 1.18, offsetY: -0.1 },
  cosm_acc_scarf: { scale: 1.2, offsetX: -0.08, offsetY: -0.12 },
};

export class AccessoryLayer {
  readonly container: Container;
  private gfx: Graphics = new Graphics();
  private sprite: Sprite | null = null;
  private currentAccessory: string | null = null;
  private currentRadius: number = 0;
  private currentIsLocal: boolean = false;
  private currentColor: number = 0xffffff;

  constructor() {
    this.container = new Container();
    this.container.addChild(this.gfx);
  }

  update(appearance: PublicPlayerAppearance | null, radius: number, isLocal: boolean): void {
    const accessoryId = appearance?.accessoryId ?? null;
    const color = parseHexColor(appearance?.accessoryColor) ?? 0xffffff;

    if (accessoryId === this.currentAccessory && radius === this.currentRadius
      && isLocal === this.currentIsLocal) {
      if (color === this.currentColor) return;
      this.currentColor = color;
      this.container.tint = color;
      return;
    }

    this.currentAccessory = accessoryId;
    this.currentRadius = radius;
    this.currentIsLocal = isLocal;
    this.currentColor = color;
    const g = this.gfx;
    g.clear();
    this.detachSprite();

    if (!accessoryId) {
      this.container.visible = false;
      return;
    }

    this.container.visible = true;
    this.container.tint = color;

    const texture = AssetManager.getAccessoryTexture(accessoryId);
    if (texture) {
      const layout = ACCESSORY_LAYOUTS[accessoryId] ?? { scale: 1.2, offsetY: -0.12 };
      const size = radius * 2 * layout.scale;
      this.sprite = new Sprite(texture);
      this.sprite.anchor.set(0.5);
      this.sprite.position.set(
        radius * (layout.offsetX ?? 0) * 2,
        radius * layout.offsetY * 2,
      );
      this.sprite.width = size;
      this.sprite.height = size;
      this.container.addChild(this.sprite);
      return;
    }

    const s = radius;
    const lineW = Math.max(1, s * 0.12);

    switch (accessoryId) {
      case 'cosm_acc_round_glasses': {
        g.circle(-s * 0.35, -s * 0.15, s * 0.3);
        g.stroke({ width: lineW, color: 0x333333, alpha: 0.8 });
        g.circle(s * 0.35, -s * 0.15, s * 0.3);
        g.stroke({ width: lineW, color: 0x333333, alpha: 0.8 });
        g.moveTo(-s * 0.05, -s * 0.15);
        g.lineTo(s * 0.05, -s * 0.15);
        g.stroke({ width: lineW, color: 0x333333, alpha: 0.8 });
        break;
      }
      case 'cosm_acc_sunglasses': {
        g.roundRect(-s * 0.55, -s * 0.2, s * 0.55, s * 0.25, s * 0.08);
        g.fill({ color: 0x111111, alpha: 0.85 });
        g.roundRect(0, -s * 0.2, s * 0.55, s * 0.25, s * 0.08);
        g.fill({ color: 0x111111, alpha: 0.85 });
        g.moveTo(0, -s * 0.2);
        g.lineTo(0, -s * 0.05);
        g.stroke({ width: lineW, color: 0x111111, alpha: 0.7 });
        break;
      }
      case 'cosm_acc_headband': {
        g.arc(0, -s * 0.1, s * 0.85, -Math.PI * 0.9, Math.PI * 0.9);
        g.stroke({ width: Math.max(2, s * 0.15), color: 0xffffff, alpha: 0.4 });
        g.arc(0, -s * 0.1, s * 0.85, -Math.PI * 0.9, Math.PI * 0.9);
        g.stroke({ width: Math.max(1, s * 0.08), color: 0xff4444, alpha: 0.5 });
        break;
      }
      default: {
        warnCosmeticFallback('accessory', accessoryId);
        this.container.visible = false;
        break;
      }
    }
  }

  setVisible(visible: boolean): void {
    this.container.visible = visible;
  }

  destroy(): void {
    this.gfx.clear();
    this.gfx.destroy();
    this.detachSprite();
    this.container.removeChildren();
  }

  private detachSprite(): void {
    if (!this.sprite) return;
    this.container.removeChild(this.sprite);
    this.sprite.destroy({ texture: false });
    this.sprite = null;
  }
}

function parseHexColor(value: string | null | undefined): number | null {
  if (!value) return null;
  if (!/^#[0-9A-Fa-f]{6}$/.test(value)) return null;
  return parseInt(value.slice(1), 16);
}
